import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { io } from 'socket.io-client';

const API_BASE = process.env.REACT_APP_API_URL || window.location.origin;

const CallContext = createContext(null);
export const useCall = () => useContext(CallContext);

const CALL_STATES = { IDLE: 'idle', RINGING: 'ringing', CONNECTING: 'connecting', ACTIVE: 'active', ENDED: 'ended' };
const RING_TIMEOUT = 45000;

export default function CallProvider({ userId, userType = 'user', userName = '', children }) {
  const [callState, setCallState] = useState(CALL_STATES.IDLE);
  const [callInfo, setCallInfo] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOn, setIsVideoOn] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [callMethod, setCallMethod] = useState(null);
  const [connected, setConnected] = useState(false);

  const socketRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const timerRef = useRef(null);
  const ringTimeoutRef = useRef(null);

  useEffect(() => {
    if (!userId) return;

    const socket = io(`${API_BASE}/chat`, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      if (userType === 'team' || userType === 'astrologer') {
        socket.emit('team:join', { teamId: userId, name: userName, role: userType });
      } else {
        socket.emit('user:join', userId);
      }
    });

    socket.on('disconnect', () => setConnected(false));

    // Incoming WebRTC call
    socket.on('call:incoming', (data) => {
      setCallInfo({
        callId: data.callId || `call_${Date.now()}`,
        from: data.from,
        fromName: data.fromName || 'Unknown',
        callType: data.callType || 'audio',
        direction: 'incoming',
        method: 'webrtc',
      });
      setCallMethod('webrtc');
      setCallState(CALL_STATES.RINGING);
    });

    // Incoming Jitsi call
    socket.on('call:jitsi-incoming', (data) => {
      setCallInfo({
        callId: data.callId || `call_${Date.now()}`,
        from: data.from,
        fromName: data.fromName || 'Unknown',
        callType: data.callType || 'audio',
        jitsiRoom: data.jitsiRoom,
        direction: 'incoming',
        method: 'jitsi',
      });
      setCallMethod('jitsi');
      setCallState(CALL_STATES.RINGING);
    });

    // Call accepted by remote
    socket.on('call:answered', (data) => {
      setCallState(CALL_STATES.ACTIVE);
      if (data?.jitsiRoom) {
        setCallInfo(prev => prev ? { ...prev, jitsiRoom: data.jitsiRoom } : prev);
      }
    });

    socket.on('call:jitsi-accepted', (data) => {
      setCallState(CALL_STATES.ACTIVE);
      if (data?.jitsiRoom) {
        setCallInfo(prev => prev ? { ...prev, jitsiRoom: data.jitsiRoom } : prev);
      }
    });

    // Call rejected or ended by remote
    socket.on('call:rejected', () => endCallCleanup('rejected'));
    socket.on('call:ended', () => endCallCleanup('ended'));
    socket.on('call:jitsi-ended', () => endCallCleanup('ended'));
    socket.on('call:jitsi-declined', () => endCallCleanup('declined'));
    socket.on('call:peer-offline', () => endCallCleanup('offline'));
    socket.on('call:no-answer', () => endCallCleanup('no-answer'));
    socket.on('call:jitsi-no-answer', () => endCallCleanup('no-answer'));
    socket.on('call:claimed-by-other', () => endCallCleanup('claimed'));
    socket.on('call:jitsi-claimed-by-other', () => endCallCleanup('claimed'));

    // WebRTC signal relay
    socket.on('webrtc:signal', (data) => {
      if (window._vtdWebRTCSignalHandler) {
        window._vtdWebRTCSignalHandler(data);
      }
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, userType, userName]);

  // Call duration timer
  useEffect(() => {
    if (callState === CALL_STATES.ACTIVE) {
      setCallDuration(0);
      const start = Date.now();
      timerRef.current = setInterval(() => {
        setCallDuration(Math.floor((Date.now() - start) / 1000));
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [callState]);

  const endCallCleanup = useCallback((reason) => {
    if (ringTimeoutRef.current) clearTimeout(ringTimeoutRef.current);
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
    remoteStreamRef.current = null;
    setCallState(CALL_STATES.ENDED);
    setCallInfo(prev => prev ? { ...prev, endReason: reason } : null);
    setTimeout(() => {
      setCallState(CALL_STATES.IDLE);
      setCallInfo(null);
      setIsMuted(false);
      setIsVideoOn(false);
      setCallDuration(0);
      setCallMethod(null);
    }, 2000);
  }, []);

  const startCall = useCallback(async (targetId, callType = 'audio', method = 'jitsi') => {
    if (callState !== CALL_STATES.IDLE || !socketRef.current) return;

    const callId = `call_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const info = {
      callId,
      to: targetId,
      callType,
      direction: 'outgoing',
      method,
      fromName: userName || userId,
    };

    if (method === 'jitsi') {
      const jitsiRoom = `VTD-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      info.jitsiRoom = jitsiRoom;
      setCallInfo(info);
      setCallMethod('jitsi');
      setCallState(CALL_STATES.RINGING);

      if (userType === 'team' || userType === 'astrologer') {
        socketRef.current.emit('call:jitsi-start', {
          to: targetId,
          jitsiRoom,
          callType,
          teamName: userName,
        });
      } else {
        socketRef.current.emit('call:jitsi-start-team', {
          roomName: jitsiRoom,
          callerName: userName || userId,
        });
      }
    } else {
      try {
        const constraints = { audio: true, video: callType === 'video' };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        localStreamRef.current = stream;
        setIsVideoOn(callType === 'video');
      } catch (e) {
        console.error('[Call] getUserMedia failed:', e);
        return;
      }

      setCallInfo(info);
      setCallMethod('webrtc');
      setCallState(CALL_STATES.RINGING);

      if (userType === 'team' || userType === 'astrologer') {
        socketRef.current.emit('call:request', {
          to: targetId,
          callType,
          teamName: userName,
        });
      } else {
        socketRef.current.emit('call:request-team', {
          userId,
          callType,
          userName: userName || userId,
        });
      }
    }

    ringTimeoutRef.current = setTimeout(() => {
      if (callState === CALL_STATES.RINGING) {
        endCallCleanup('no-answer');
      }
    }, RING_TIMEOUT);
  }, [callState, userId, userName, userType, endCallCleanup]);

  const answerCall = useCallback(async () => {
    if (callState !== CALL_STATES.RINGING || !callInfo || !socketRef.current) return;

    if (callInfo.method === 'jitsi') {
      setCallState(CALL_STATES.ACTIVE);
      socketRef.current.emit('call:jitsi-accept', {
        to: callInfo.from,
        jitsiRoom: callInfo.jitsiRoom,
        teamName: userName,
      });
    } else {
      try {
        const constraints = { audio: true, video: callInfo.callType === 'video' };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        localStreamRef.current = stream;
        setIsVideoOn(callInfo.callType === 'video');
      } catch (e) {
        console.error('[Call] getUserMedia failed:', e);
        return;
      }

      setCallState(CALL_STATES.CONNECTING);
      socketRef.current.emit('call:answer', {
        to: callInfo.from,
        callType: callInfo.callType,
      });
    }
  }, [callState, callInfo, userName]);

  const rejectCall = useCallback(() => {
    if (!callInfo || !socketRef.current) return;
    if (callInfo.method === 'jitsi') {
      socketRef.current.emit('call:jitsi-decline', { to: callInfo.from });
    } else {
      socketRef.current.emit('call:reject', { to: callInfo.from });
    }
    endCallCleanup('rejected');
  }, [callInfo, endCallCleanup]);

  const endCall = useCallback(() => {
    if (!callInfo || !socketRef.current) return;
    if (callInfo.method === 'jitsi') {
      socketRef.current.emit('call:jitsi-end', {
        to: callInfo.to || callInfo.from,
        jitsiRoom: callInfo.jitsiRoom,
      });
    } else {
      socketRef.current.emit('call:end', { to: callInfo.to || callInfo.from });
    }
    endCallCleanup('ended');
  }, [callInfo, endCallCleanup]);

  const toggleMute = useCallback(() => {
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach(t => { t.enabled = !t.enabled; });
    }
    setIsMuted(m => !m);
  }, []);

  const toggleVideo = useCallback(async () => {
    if (!localStreamRef.current) return;
    const videoTracks = localStreamRef.current.getVideoTracks();
    if (videoTracks.length > 0) {
      videoTracks.forEach(t => { t.enabled = !t.enabled; });
      setIsVideoOn(v => !v);
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        stream.getVideoTracks().forEach(t => localStreamRef.current.addTrack(t));
        setIsVideoOn(true);
      } catch (e) {
        console.error('[Call] Video toggle failed:', e);
      }
    }
  }, []);

  const value = {
    callState,
    callInfo,
    isMuted,
    isVideoOn,
    callDuration,
    callMethod,
    connected,
    localStream: localStreamRef,
    remoteStream: remoteStreamRef,
    socket: socketRef,
    startCall,
    answerCall,
    rejectCall,
    endCall,
    toggleMute,
    toggleVideo,
    CALL_STATES,
  };

  return (
    <CallContext.Provider value={value}>
      {children}
    </CallContext.Provider>
  );
}
